/**
 * tracking.ts — Package tracking board interactions — Phase 196
 *
 * Paste bar: POST code/label/merchant (+ carrier if picker shown) to data-add-url
 * (handleAddTracking signal). On response:
 *   - {duplicate:true, id}    → scroll-to + highlight existing parcel (D-08)
 *   - {needsCarrier:true}     → reveal carrier picker, re-POST with chosen int carrier (D-07)
 *   - {id, duplicate:false}   → reload the board to show new parcel
 *   - {error/message}         → show status message inline
 *
 * Accordion: Bootstrap Collapse for per-parcel event history (data-bs-toggle already in Latte).
 * Module initialises on the /tracking board page (#tracking-board) AND the /tracking/<id>
 * detail page (#tracking-detail) — the link-transaction picker (Plan 04) lives on both.
 *
 * Link-transaction picker (D-09/D-10/D-11, Plan 04): a single shared Bootstrap Modal
 * (#tracking-link-modal) populated via event delegation on document.body for
 * .js-link-transaction triggers. Fetches candidates + currently-linked transactions from
 * data-candidates-url (handleLinkCandidates), POSTs link/unlink to data-link-url /
 * data-unlink-url. Search box debounced 300ms. Candidate/linked row text is HTML-escaped
 * before innerHTML insertion (T-196-15 — transaction name is user-controlled bank data).
 *
 * Phase 198 additions (Plan 06):
 *   - Reload-survival (Pitfall 4): `_submitAdd()`'s success branch stashes the response's
 *     `autoLink` payload (HIGH auto-linked transaction OR MEDIUM suggestions) in
 *     sessionStorage keyed by the new parcel id BEFORE the existing unconditional
 *     `window.location.reload()`. `init()` reads + one-shot-deletes any pending entry and
 *     renders it into that card's `.js-parcel-suggestion-slot` (added in Plan 05).
 *   - HIGH confirmation: '✓ Auto-linked: … [undo]' — undo reuses the existing
 *     unlinkTransaction POST (data-unlink-url already on each card's Link-transaction button).
 *   - MEDIUM suggestion chip: 'Suggested: … [Confirm] [Dismiss] [See other candidates]' —
 *     Confirm/Dismiss POST to `#tracking-board`'s data-confirm-url/data-dismiss-url;
 *     "See other candidates" clicks the card's existing .js-link-transaction button to reuse
 *     the SAME link-picker modal (no new picker surface, D-04/D-06).
 *   - Return direction: the paste-bar "This is a return" checkbox is folded into
 *     `_submitAdd()`'s POST body (direction=outbound when checked); a `.js-flip-direction`
 *     delegated handler POSTs trackingId+direction to data-flip-url (D-07).
 *   - Auto badge (D-16): `_renderLinkedList()` appends a distinct `auto` badge span when
 *     `t.link_method === 'auto'`, alongside the existing 'Linked' badge.
 *   - All new client-rendered merchant/amount/date strings pass through `escapeHtml()`
 *     before innerHTML insertion (bank-adjacent data, same discipline as Plan 04).
 *
 * Bootstrap is NOT on window — import explicitly.
 * All signal URLs come from data-* attributes set in Latte ({link addTracking!}, etc).
 */

import { Collapse, Modal } from 'bootstrap';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/timeline';
import { getChartTheme } from './utils/chartTheme';
import DataTable from 'datatables.net-bs5';

// Phase 200 (TRACK-17): highcharts/modules/timeline registers itself as a side-effect import
// above. Guard against a failed registration so a broken bundle fails loudly instead of
// silently rendering nothing.
if (typeof (Highcharts as any).seriesTypes?.['timeline'] === 'undefined') {
    console.error('[timeline] module failed to register — parcel timeline chart will not work.');
}

// Phase 200 (TRACK-17): timeline chart data — Tracking/detail.latte's inline <script> in
// {block content} (ASC-ordered, one point per tracking_events row).
declare var tracking_events_chart: Array<{
    occurred_at_ms: number;
    status_label: string;
    description: string;
    location: string | null;
}> | undefined;

// Phase 200 (TRACK-14): analytics bar-chart data — Tracking/analytics.latte's inline <script>.
declare var tracking_analytics_by_carrier: Array<{ label: string; avg_days: number; n: number }> | undefined;
declare var tracking_analytics_by_merchant: Array<{ label: string; avg_days: number; n: number }> | undefined;

// Phase 198: sessionStorage key prefix for the reload-survival bridge (Pitfall 4).
// One-shot — read once in init() then deleted, regardless of render outcome.
const AUTOLINK_STORAGE_PREFIX = 'tracking198:autolink:';

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export default {

    _inited: false,

    // ── Link-transaction picker state (set on modal open, Plan 04) ─────────────
    _linkModalInstance: null as InstanceType<typeof Modal> | null,
    _linkTrackingId: '',
    _linkCandidatesUrl: '',
    _linkUrl: '',
    _linkUnlinkUrl: '',
    _linkSearchDebounce: null as ReturnType<typeof setTimeout> | null,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice
        if (this._inited) return;

        // Activate on the board page, the detail page (link picker lives on both, Plan 04),
        // OR the analytics page (Phase 200 — widened so /tracking/analytics initializes).
        const board = document.getElementById('tracking-board');
        const detail = document.getElementById('tracking-detail');
        const analytics = document.getElementById('tracking-analytics');
        if (!board && !detail && !analytics) return;

        this._inited = true;

        if (board) {
            const addUrl = board.dataset.addUrl ?? '';

            // Phase 200 (D-11/D-12): share_target prefill — PROPERTY assignment only, never
            // innerHTML (Pitfall 8 — the /tracking?shared=... URL is an ordinary reachable GET
            // endpoint, not gated by the OS share sheet). No auto-submit — operator reviews
            // and hits Enter (D-11).
            const sharedPrefill = board.dataset.sharedPrefill ?? '';
            if (sharedPrefill) {
                const codeInput = document.getElementById('tracking-code-input') as HTMLInputElement | null;
                if (codeInput) {
                    codeInput.value = sharedPrefill;
                    codeInput.focus();
                }
            }

            // ── Paste bar form submit ───────────────────────────────────────────
            const form = document.getElementById('tracking-paste-form') as HTMLFormElement | null;
            if (form) {
                form.addEventListener('submit', (e: Event) => {
                    e.preventDefault();
                    this._submitAdd(addUrl, null);
                });
            }

            // ── Carrier picker: submit with chosen carrier ──────────────────────
            const carrierSubmit = document.getElementById('tracking-carrier-submit');
            if (carrierSubmit) {
                carrierSubmit.addEventListener('click', () => {
                    const select = document.getElementById('tracking-carrier-select') as HTMLSelectElement | null;
                    const carrier = select?.value ?? '';
                    if (!carrier) {
                        this._showStatus('Please select a carrier.', 'text-warning');
                        return;
                    }
                    this._submitAdd(addUrl, carrier);
                });
            }

            // ── Carrier picker: cancel ──────────────────────────────────────────
            const carrierCancel = document.getElementById('tracking-carrier-cancel');
            if (carrierCancel) {
                carrierCancel.addEventListener('click', () => {
                    this._hideCarrierPicker();
                });
            }

            // Initialise any Collapse elements already on the page (Bootstrap needs these for
            // toggle behaviour; data-bs-toggle handles it natively, but explicit init ensures
            // no missed elements on reload-without-navigate scenarios).
            board.querySelectorAll('.accordion-collapse').forEach((el: Element) => {
                new Collapse(el, { toggle: false });
            });
        }

        // Link-transaction picker — active on both board + detail pages (Plan 04)
        this._initLinkPicker();

        // Phase 198: auto-link confirmation/suggestion chip + flip-direction handlers —
        // active on both pages (flip control also renders on the detail page header).
        this._initSuggestionAndFlipHandlers();

        // Phase 200 (TRACK-17): parcel event timeline chart — detail page only.
        if (detail) {
            this._initTimelineChart();
        }

        // Phase 200 (TRACK-14): per-carrier/per-merchant bar charts + DataTables — analytics page only.
        if (analytics) {
            this._initAnalyticsCharts();
        }

        // Phase 198 (Pitfall 4): render any pending auto-link result stashed before the
        // reload that just brought us here (board page only — that's where paste/reload
        // happens; on the detail page no pending key will ever match, this is a cheap no-op).
        if (board) {
            this._renderPendingAutoLinkResults();
        }
    },

    // ── Timeline chart (TRACK-17/D-08/D-09/D-10, Phase 200) ─────────────────────

    /**
     * Renders the Highcharts `timeline` series into #tracking-timeline-chart on the parcel
     * detail page, from the global `tracking_events_chart` (ASC-ordered, one point per
     * tracking_events row — set by Tracking/detail.latte). Sits ABOVE the retained readable
     * `.timeline` list (D-09) — does not replace it. Inverted (vertical) on mobile (<576px).
     * All chrome via Highcharts.merge(getChartTheme(), {...}) — the UTC display setting is
     * global (src/index.ts) and is never overridden per-chart here.
     */
    _initTimelineChart(): void {
        const container = document.getElementById('tracking-timeline-chart');
        if (!container) return;
        if (typeof tracking_events_chart === 'undefined' || tracking_events_chart.length === 0) return;

        Highcharts.chart('tracking-timeline-chart', Highcharts.merge(getChartTheme(), {
            chart: { type: 'timeline', inverted: window.innerWidth < 576 },
            xAxis: { visible: false, type: 'datetime' },
            yAxis: { visible: false },
            title: { text: '' },
            legend: { enabled: false },
            series: [{
                type: 'timeline',
                data: tracking_events_chart.map((ev) => ({
                    x: ev.occurred_at_ms,
                    name: ev.status_label,
                    label: ev.status_label,
                    description: ev.description + (ev.location ? ' — ' + ev.location : ''),
                })),
            }],
        } as any));
    },

    // ── Analytics bar charts + DataTables (TRACK-14/D-05/D-06, Phase 200) ───────

    /**
     * Renders the two per-carrier/per-merchant horizontal bar charts and attaches DataTable
     * sort/search to their matching tables on /tracking/analytics. Guards each container's
     * presence independently — either section can legitimately be in its empty state
     * (Tracking/analytics.latte only renders the chart+table markup when count() > 0).
     */
    _initAnalyticsCharts(): void {
        this._renderAnalyticsChart(
            'chart-carrier',
            typeof tracking_analytics_by_carrier !== 'undefined' ? tracking_analytics_by_carrier : [],
        );
        this._renderAnalyticsChart(
            'chart-merchant',
            typeof tracking_analytics_by_merchant !== 'undefined' ? tracking_analytics_by_merchant : [],
        );

        if (document.querySelector('.datatable[data-table="tracking_analytics_carrier"]')) {
            new DataTable('.datatable[data-table="tracking_analytics_carrier"]', {
                paging: false,
                searching: true,
                info: false,
                order: [[1, 'asc']],
            });
        }
        if (document.querySelector('.datatable[data-table="tracking_analytics_merchant"]')) {
            new DataTable('.datatable[data-table="tracking_analytics_merchant"]', {
                paging: false,
                searching: true,
                info: false,
                order: [[1, 'asc']],
            });
        }
    },

    /**
     * Shared horizontal bar-chart renderer for the carrier/merchant analytics sections.
     * Highcharts.merge(getChartTheme(), {...}) — mirrors src/js/trakt.ts's genre bar chart.
     */
    _renderAnalyticsChart(elId: string, rows: Array<{ label: string; avg_days: number; n: number }>): void {
        const el = document.getElementById(elId);
        if (!el || !rows || rows.length === 0) return;

        Highcharts.chart(el, Highcharts.merge(getChartTheme(), {
            chart: { type: 'bar', backgroundColor: 'transparent' },
            title: { text: '' },
            xAxis: { categories: rows.map((r) => r.label) },
            yAxis: { title: { text: 'Avg transit days' } },
            legend: { enabled: false },
            series: [{ type: 'bar', name: 'Avg days', data: rows.map((r) => r.avg_days) }],
            credits: { enabled: false },
            plotOptions: { bar: { borderRadius: 2 } },
        } as any));
    },

    // ── Link-transaction picker (D-09/D-10/D-11, Plan 04) ──────────────────────

    _initLinkPicker(): void {
        const modalEl = document.getElementById('tracking-link-modal');
        if (!modalEl) return;

        const searchInput = document.getElementById('tracking-link-search') as HTMLInputElement | null;
        const resultsEl   = document.getElementById('tracking-link-results');
        const currentEl   = document.getElementById('tracking-link-current');

        // Open the picker — event delegation so it works for every card's button
        document.body.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-link-transaction');
            if (btn) {
                e.preventDefault();
                this._linkTrackingId    = btn.dataset.trackingId ?? '';
                this._linkCandidatesUrl = btn.dataset.candidatesUrl ?? '';
                this._linkUrl           = btn.dataset.linkUrl ?? '';
                this._linkUnlinkUrl     = btn.dataset.unlinkUrl ?? '';

                if (searchInput) searchInput.value = '';
                if (resultsEl) resultsEl.innerHTML = '<p class="small text-muted mb-0">Loading candidates…</p>';
                if (currentEl) currentEl.innerHTML = '';

                if (!this._linkModalInstance) {
                    this._linkModalInstance = new Modal(modalEl);
                }
                this._linkModalInstance.show();

                this._fetchLinkCandidates('');
                return;
            }

            // Unlink control inside the "currently linked" list
            const unlinkBtn = (e.target as HTMLElement).closest<HTMLElement>('.js-unlink-transaction');
            if (unlinkBtn) {
                e.preventDefault();
                const transactionId = unlinkBtn.dataset.transactionId ?? '';
                this._postLinkAction(this._linkUnlinkUrl, transactionId, () => {
                    this._fetchLinkCandidates(searchInput?.value.trim() ?? '');
                });
                return;
            }

            // Candidate row click → link it
            const candidateRow = (e.target as HTMLElement).closest<HTMLElement>('.js-candidate-row');
            if (candidateRow) {
                e.preventDefault();
                const transactionId = candidateRow.dataset.transactionId ?? '';
                this._postLinkAction(this._linkUrl, transactionId, () => {
                    this._fetchLinkCandidates(searchInput?.value.trim() ?? '');
                });
            }
        });

        // Debounced search box → re-fetch candidates
        if (searchInput) {
            searchInput.addEventListener('input', () => {
                if (this._linkSearchDebounce) clearTimeout(this._linkSearchDebounce);
                this._linkSearchDebounce = setTimeout(() => {
                    this._fetchLinkCandidates(searchInput.value.trim());
                }, 300);
            });
        }
    },

    _fetchLinkCandidates(search: string): void {
        const resultsEl = document.getElementById('tracking-link-results');
        const currentEl = document.getElementById('tracking-link-current');
        if (!this._linkCandidatesUrl || !this._linkTrackingId) return;

        const body = new URLSearchParams();
        body.set('trackingId', this._linkTrackingId);
        if (search) body.set('search', search);

        fetch(this._linkCandidatesUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            this._renderLinkedList(currentEl, data.linked ?? []);
            this._renderCandidateList(resultsEl, data.candidates ?? []);
        })
        .catch(() => {
            if (resultsEl) resultsEl.innerHTML = '<p class="small text-danger mb-0">Failed to load candidates. Please try again.</p>';
        });
    },

    /**
     * WR-02: format a transaction date for display. transactions.date is a pure calendar
     * DATE; Nette serializes it as a full ISO-8601 string (e.g. 2026-07-01T00:00:00-04:00),
     * which previously rendered verbatim in the picker/banner/chip. Render it as a local
     * calendar date ('Jul 1, 2026') from the y-m-d components — deliberately NOT via
     * new Date(isoString), which anchors at the offset midnight and can shift the day.
     * Falls back to the raw string if it is not ISO-shaped.
     */
    _formatTxnDate(raw: any): string {
        const s = String(raw ?? '');
        if (!s) return '';
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return s;
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        if (isNaN(d.getTime())) return s;
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    /**
     * IN-01: money surfaces render 2-decimal (e.g. '25.00', '48.10') to match every
     * other amount in the app (|number:2 / number_format($x, 2)). Falls back to the
     * raw string when unparseable, mirroring _formatTxnDate's graceful-degrade.
     */
    _formatAmount(raw: any): string {
        const s = String(raw ?? '');
        if (s === '') return '';
        const n = Number(raw);
        return isNaN(n) ? s : n.toFixed(2);
    },

    _renderLinkedList(container: HTMLElement | null, linked: any[]): void {
        if (!container) return;
        if (!linked.length) {
            container.innerHTML = '';
            return;
        }
        const rows = linked.map((t: any) => {
            const name   = escapeHtml(String(t.name ?? ''));
            const amount = escapeHtml(this._formatAmount(t.amount));  // IN-01: 2-decimal money
            const date   = escapeHtml(this._formatTxnDate(t.date));  // WR-02: 'Jul 1, 2026', not raw ISO
            const id     = Number(t.id) || 0;
            // Phase 198 (D-16, Pitfall 5 render half): distinct 'auto' badge alongside the
            // existing 'Linked' badge when this row was written by autoLink() unattended,
            // not operator-confirmed. Manual links stay unbadged (t.link_method === 'manual').
            const autoBadge = t.link_method === 'auto'
                ? '<span class="badge text-bg-info me-2">auto</span>'
                : '';
            return '<div class="list-group-item d-flex justify-content-between align-items-center py-2">'
                 + '<span><span class="badge text-bg-success me-2">Linked</span>' + autoBadge + name
                 + '<div class="text-muted small">' + date + ' · $' + amount + '</div></span>'
                 + '<button type="button" class="btn btn-sm btn-outline-danger js-unlink-transaction" '
                 + 'data-transaction-id="' + id + '" style="min-height:44px">Unlink</button>'
                 + '</div>';
        }).join('');
        container.innerHTML = '<p class="small fw-semibold mb-1">Currently linked</p><div class="list-group mb-2">' + rows + '</div>';
    },

    _renderCandidateList(container: HTMLElement | null, candidates: any[]): void {
        if (!container) return;
        if (!candidates.length) {
            container.innerHTML = '<p class="small text-muted mb-0">No candidate transactions found.</p>';
            return;
        }
        const rows = candidates.map((c: any) => {
            const name   = escapeHtml(String(c.name ?? ''));
            const amount = escapeHtml(this._formatAmount(c.amount));  // IN-01: 2-decimal money
            const date   = escapeHtml(this._formatTxnDate(c.date));  // WR-02: 'Jul 1, 2026', not raw ISO
            const id     = Number(c.id) || 0;
            return '<button type="button" class="list-group-item list-group-item-action js-candidate-row '
                 + 'd-flex justify-content-between align-items-center" data-transaction-id="' + id
                 + '" style="min-height:44px"><span>' + name
                 + '<div class="text-muted small">' + date + '</div></span>'
                 + '<span class="fw-semibold">$' + amount + '</span></button>';
        }).join('');
        container.innerHTML = '<p class="small fw-semibold mb-1">Recent candidates</p><div class="list-group">' + rows + '</div>';
    },

    _postLinkAction(url: string, transactionId: string, onDone: () => void): void {
        if (!url || !transactionId || !this._linkTrackingId) return;

        const body = new URLSearchParams();
        body.set('trackingId', this._linkTrackingId);
        body.set('transactionId', transactionId);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then(() => {
            // Idempotent — refresh the picker's lists regardless of exact response shape
            onDone();
        })
        .catch(() => {
            // Silent-fail on network error (idempotent retry via re-opening the picker)
        });
    },

    // ── Phase 198: auto-link confirmation, suggestion chip, flip-direction (D-04/D-06/D-07) ──

    /**
     * Delegated click handler covering all Phase 198 card-level controls: the HIGH
     * auto-link banner's [undo], the MEDIUM suggestion chip's [Confirm]/[Dismiss]/
     * [See other candidates], and the flip-direction button. All live inside cards
     * rendered from the shared parcel-card.latte partial (Plan 05), so one delegated
     * listener on document.body covers every parcel on both the board and detail pages.
     */
    _initSuggestionAndFlipHandlers(): void {
        document.body.addEventListener('click', (e: Event) => {
            const target = e.target as HTMLElement;

            const undoBtn = target.closest<HTMLElement>('.js-autolink-undo');
            if (undoBtn) {
                e.preventDefault();
                this._undoAutoLink(undoBtn);
                return;
            }

            const confirmBtn = target.closest<HTMLElement>('.js-suggestion-confirm');
            if (confirmBtn) {
                e.preventDefault();
                this._confirmSuggestion(confirmBtn);
                return;
            }

            const dismissBtn = target.closest<HTMLElement>('.js-suggestion-dismiss');
            if (dismissBtn) {
                e.preventDefault();
                this._dismissSuggestion(dismissBtn);
                return;
            }

            const otherBtn = target.closest<HTMLElement>('.js-suggestion-other');
            if (otherBtn) {
                e.preventDefault();
                // Reuse the EXISTING link-picker modal (Plan 04) — no new picker surface
                // (D-04/D-06). Simplest correct reuse: click the card's own
                // .js-link-transaction trigger, which already owns the open+fetch flow.
                const trackingId = otherBtn.dataset.trackingId ?? '';
                const card = document.getElementById('parcel-' + trackingId);
                const linkBtn = card?.querySelector<HTMLElement>('.js-link-transaction');
                if (linkBtn) linkBtn.click();
                return;
            }

            const flipBtn = target.closest<HTMLElement>('.js-flip-direction');
            if (flipBtn) {
                e.preventDefault();
                this._flipDirection(flipBtn);
                return;
            }
        });
    },

    /**
     * Undo a HIGH-confidence auto-link (D-06). Reuses the existing unlinkTransaction
     * POST — the URL is read off the same card's .js-link-transaction button
     * (data-unlink-url is identical across all cards; the signal itself is
     * tracking-id-agnostic, trackingId travels in the POST body).
     */
    _undoAutoLink(btn: HTMLElement): void {
        const trackingId    = btn.dataset.trackingId ?? '';
        const transactionId = btn.dataset.transactionId ?? '';
        const card = document.getElementById('parcel-' + trackingId);
        const unlinkUrl = card?.querySelector<HTMLElement>('.js-link-transaction')?.dataset.unlinkUrl ?? '';
        if (!unlinkUrl || !trackingId || !transactionId) return;

        const body = new URLSearchParams();
        body.set('trackingId', trackingId);
        body.set('transactionId', transactionId);

        fetch(unlinkUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then(() => {
            const slot = card?.querySelector<HTMLElement>('.js-parcel-suggestion-slot');
            if (slot) slot.innerHTML = '';
        })
        .catch(() => {
            // Silent-fail on network error — operator can retry via the Link transaction button
        });
    },

    /**
     * Confirm a MEDIUM-confidence suggestion (D-04). Always logs 'manual' server-side
     * (handleConfirmSuggestion → linkTransaction($id, $id, 'manual') — D-05: a
     * human-confirmed action is never mislabeled as the unattended 'auto' method).
     */
    _confirmSuggestion(btn: HTMLElement): void {
        const trackingId    = btn.dataset.trackingId ?? '';
        const transactionId = btn.dataset.transactionId ?? '';
        const confirmUrl    = btn.dataset.confirmUrl ?? '';
        if (!confirmUrl || !trackingId || !transactionId) return;

        const body = new URLSearchParams();
        body.set('trackingId', trackingId);
        body.set('transactionId', transactionId);

        fetch(confirmUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then(() => {
            // Confirmed link now needs to show in the linked-transactions list —
            // simplest correct refresh is a reload (same idiom as the rest of the module).
            window.location.reload();
        })
        .catch(() => {
            // Silent-fail on network error
        });
    },

    /**
     * Dismiss a MEDIUM-confidence suggestion (D-04). Transient — no link row is written
     * server-side; the client just clears the chip locally on a successful response.
     */
    _dismissSuggestion(btn: HTMLElement): void {
        const trackingId    = btn.dataset.trackingId ?? '';
        const transactionId = btn.dataset.transactionId ?? '';
        const dismissUrl    = btn.dataset.dismissUrl ?? '';
        const card = document.getElementById('parcel-' + trackingId);
        if (!dismissUrl || !trackingId) return;

        const body = new URLSearchParams();
        body.set('trackingId', trackingId);
        if (transactionId) body.set('transactionId', transactionId);

        fetch(dismissUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(() => {
            const slot = card?.querySelector<HTMLElement>('.js-parcel-suggestion-slot');
            if (slot) slot.innerHTML = '';
        })
        .catch(() => {
            // Silent-fail on network error
        });
    },

    /**
     * Flip a parcel's inbound/outbound direction (D-07). POSTs the toggled value to
     * data-flip-url; on success, reload so the ↩ Return badge, the flip button's own
     * label, and (for returns) the credit-side link candidates all reflect the change.
     */
    _flipDirection(btn: HTMLElement): void {
        const trackingId       = btn.dataset.trackingId ?? '';
        const flipUrl          = btn.dataset.flipUrl ?? '';
        const currentDirection = btn.dataset.currentDirection ?? 'inbound';
        const newDirection     = currentDirection === 'outbound' ? 'inbound' : 'outbound';
        if (!trackingId || !flipUrl) return;

        const body = new URLSearchParams();
        body.set('trackingId', trackingId);
        body.set('direction', newDirection);

        fetch(flipUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data && data.success) {
                window.location.reload();
            }
        })
        .catch(() => {
            // Silent-fail on network error
        });
    },

    /**
     * Reload-survival bridge, write side (Pitfall 4). Called from _submitAdd()'s success
     * branch BEFORE window.location.reload() fires. Stashes exactly what the reloaded
     * page's init() needs to render the HIGH banner or MEDIUM chip — never the raw
     * autoLink payload verbatim, so the read side has a small, stable shape to trust.
     */
    _stashAutoLinkResult(newParcelId: number | string, autoLink: any): void {
        if (!autoLink) return;
        try {
            if (autoLink.autoLinked === true && autoLink.transaction) {
                const t = autoLink.transaction;
                sessionStorage.setItem(AUTOLINK_STORAGE_PREFIX + newParcelId, JSON.stringify({
                    type: 'high',
                    merchant: t.name ?? '',
                    amount: t.amount ?? '',
                    date: t.date ?? '',
                    transactionId: t.id ?? 0,
                }));
            } else if (Array.isArray(autoLink.suggestions) && autoLink.suggestions.length > 0) {
                sessionStorage.setItem(AUTOLINK_STORAGE_PREFIX + newParcelId, JSON.stringify({
                    type: 'medium',
                    suggestions: autoLink.suggestions.map((s: any) => ({
                        merchant: s.name ?? '',
                        amount: s.amount ?? '',
                        date: s.date ?? '',
                        transactionId: s.id ?? 0,
                    })),
                }));
            }
            // candidates === 0 (no match / outbound / already-linked / no merchant) →
            // nothing to stash, deliberate no-op.
        } catch (e) {
            // sessionStorage unavailable (private-mode quota, etc.) — non-fatal; the
            // confirmation/chip simply won't render post-reload, the link itself already
            // happened server-side for the HIGH case.
        }
    },

    /**
     * Reload-survival bridge, read side (Pitfall 4). Called once from init() on the
     * board page. Scans for any pending entry (there is at most one — the parcel just
     * created by the paste that triggered this reload), renders it into that card's
     * .js-parcel-suggestion-slot, then deletes the key regardless of render outcome
     * (one-shot — a stale entry must never resurface on a later, unrelated reload).
     */
    _renderPendingAutoLinkResults(): void {
        const pendingKeys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.indexOf(AUTOLINK_STORAGE_PREFIX) === 0) {
                pendingKeys.push(key);
            }
        }

        pendingKeys.forEach((key) => {
            const trackingId = key.slice(AUTOLINK_STORAGE_PREFIX.length);
            const raw = sessionStorage.getItem(key);
            sessionStorage.removeItem(key); // one-shot, regardless of what follows

            if (!raw) return;
            let payload: any;
            try {
                payload = JSON.parse(raw);
            } catch (e) {
                return;
            }

            const card = document.getElementById('parcel-' + trackingId);
            const slot = card?.querySelector<HTMLElement>('.js-parcel-suggestion-slot');
            if (!slot) return;

            if (payload.type === 'high') {
                this._renderAutoLinkBanner(slot, trackingId, payload);
            } else if (payload.type === 'medium') {
                this._renderSuggestionChip(slot, trackingId, payload);
            }
        });
    },

    /**
     * Render the HIGH auto-link confirmation banner (D-06): '✓ Auto-linked: <merchant> ·
     * $<amount> · <date> [undo]'. All fields escaped before innerHTML (bank-adjacent data).
     */
    _renderAutoLinkBanner(slot: HTMLElement, trackingId: string, payload: any): void {
        const merchant      = escapeHtml(String(payload.merchant ?? ''));
        const amount        = escapeHtml(this._formatAmount(payload.amount));  // IN-01: 2-decimal money
        const date          = escapeHtml(this._formatTxnDate(payload.date));  // WR-02: 'Jul 1, 2026', not raw ISO
        const transactionId = Number(payload.transactionId) || 0;

        slot.innerHTML = '<div class="alert alert-success py-1 px-2 small mb-1 d-flex justify-content-between align-items-center flex-wrap gap-1">'
            + '<span>&#10003; Auto-linked: ' + merchant + ' &middot; $' + amount + ' &middot; ' + date + '</span>'
            + '<button type="button" class="btn btn-sm btn-link p-0 js-autolink-undo" '
            + 'data-tracking-id="' + escapeHtml(trackingId) + '" data-transaction-id="' + transactionId + '">undo</button>'
            + '</div>';
    },

    /**
     * Render the MEDIUM suggestion chip (D-04): 'Suggested: <merchant> · $<amount> ·
     * <date> [Confirm] [Dismiss] [See other candidates]'. Only the top (closest-date)
     * suggestion is shown inline; "See other candidates" opens the full existing picker
     * for every candidate. All fields escaped before innerHTML.
     */
    _renderSuggestionChip(slot: HTMLElement, trackingId: string, payload: any): void {
        const board       = document.getElementById('tracking-board');
        const confirmUrl  = board?.dataset.confirmUrl ?? '';
        const dismissUrl  = board?.dataset.dismissUrl ?? '';
        const suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];
        if (!suggestions.length) return;

        const first          = suggestions[0];
        const merchant       = escapeHtml(String(first.merchant ?? ''));
        const amount         = escapeHtml(this._formatAmount(first.amount));  // IN-01: 2-decimal money
        const date           = escapeHtml(this._formatTxnDate(first.date));  // WR-02: 'Jul 1, 2026', not raw ISO
        const transactionId  = Number(first.transactionId) || 0;
        const safeTrackingId = escapeHtml(trackingId);

        slot.innerHTML = '<div class="alert alert-info py-1 px-2 small mb-1 d-flex justify-content-between align-items-center flex-wrap gap-1">'
            + '<span>Suggested: ' + merchant + ' &middot; $' + amount + ' &middot; ' + date + '</span>'
            + '<span class="d-flex gap-1 flex-wrap">'
            + '<button type="button" class="btn btn-sm btn-success js-suggestion-confirm" '
            + 'data-tracking-id="' + safeTrackingId + '" data-transaction-id="' + transactionId + '" '
            + 'data-confirm-url="' + escapeHtml(confirmUrl) + '">Confirm</button>'
            + '<button type="button" class="btn btn-sm btn-outline-secondary js-suggestion-dismiss" '
            + 'data-tracking-id="' + safeTrackingId + '" data-transaction-id="' + transactionId + '" '
            + 'data-dismiss-url="' + escapeHtml(dismissUrl) + '">Dismiss</button>'
            + '<a href="#" class="btn btn-sm btn-link js-suggestion-other" '
            + 'data-tracking-id="' + safeTrackingId + '">See other candidates</a>'
            + '</span></div>';
    },

    // ── Add tracking code (paste bar POST) ────────────────────────────────────

    _submitAdd(addUrl: string, carrier: string | null): void {
        const codeInput      = document.getElementById('tracking-code-input') as HTMLInputElement | null;
        const labelInput     = document.getElementById('tracking-label-input') as HTMLInputElement | null;
        const merchantInput  = document.getElementById('tracking-merchant-input') as HTMLInputElement | null;
        // Phase 198 (D-07): "This is a return" paste-bar toggle (parcel-card.latte's
        // paste-bar checkbox, name="direction" value="outbound", added in Plan 05).
        const directionToggle = document.getElementById('tracking-direction-toggle') as HTMLInputElement | null;

        const code     = codeInput?.value.trim() ?? '';
        const label    = labelInput?.value.trim() ?? '';
        const merchant = merchantInput?.value.trim() ?? '';

        if (!code) {
            this._showStatus('Please enter a tracking code.', 'text-warning');
            return;
        }
        if (!addUrl) {
            this._showStatus('Add URL missing — page reload required.', 'text-danger');
            return;
        }

        const body = new URLSearchParams();
        body.set('code', code);
        if (label)    body.set('label', label);
        if (merchant) body.set('merchant', merchant);
        if (carrier)  body.set('carrier', carrier);
        // Phase 198 (D-07): server-side normalizes anything but exactly 'outbound' to
        // 'inbound' — the client toggle value is never trusted verbatim on that end,
        // so it's safe to send it plainly here.
        if (directionToggle?.checked) body.set('direction', 'outbound');

        this._showStatus('Adding…', 'text-muted');

        fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.duplicate && data.id) {
                // D-08: scroll to + highlight existing parcel
                this._hideCarrierPicker();
                this._hideStatus();
                this._scrollToParcel(data.id);
                return;
            }
            if (data.needsCarrier) {
                // D-07: carrier picker
                this._showStatus('', 'text-muted');
                this._showCarrierPicker();
                return;
            }
            if (data.error || data.message) {
                const msg = data.message ?? data.error ?? 'Unknown error';
                this._showStatus('Error: ' + msg, 'text-danger');
                return;
            }
            if (data.id) {
                // Phase 198 (Pitfall 4): stash the autoLink payload BEFORE the
                // unconditional reload below discards it — the HIGH confirmation banner
                // and MEDIUM suggestion chip are rendered from this stash post-reload
                // (see _renderPendingAutoLinkResults(), read side, in init()).
                if (data.autoLink) {
                    this._stashAutoLinkResult(data.id, data.autoLink);
                }
                // Success — reload board to show new parcel
                this._hideCarrierPicker();
                this._hideStatus();
                window.location.reload();
                return;
            }
            this._showStatus('Unexpected response. Please try again.', 'text-warning');
        })
        .catch((err: any) => {
            this._showStatus('Request failed. Please check your connection.', 'text-danger');
        });
    },

    // ── D-08: scroll to + briefly highlight existing parcel ───────────────────

    _scrollToParcel(id: number | string): void {
        const el = document.getElementById('parcel-' + id);
        if (!el) {
            // Parcel may be in delivered table or not visible; just reload
            window.location.reload();
            return;
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'outline 0s, outline-offset 0s';
        el.style.outline = '2px solid var(--ksat-accent)';
        el.style.outlineOffset = '2px';
        setTimeout(() => {
            el.style.outline = '';
            el.style.outlineOffset = '';
        }, 2500);
    },

    // ── D-07: carrier picker helpers ─────────────────────────────────────────

    _showCarrierPicker(): void {
        const picker = document.getElementById('tracking-carrier-picker');
        if (picker) picker.classList.remove('d-none');
    },

    _hideCarrierPicker(): void {
        const picker = document.getElementById('tracking-carrier-picker');
        if (picker) picker.classList.add('d-none');
    },

    // ── Status message helpers ────────────────────────────────────────────────

    _showStatus(msg: string, cls: string): void {
        const el = document.getElementById('tracking-status-msg');
        if (!el) return;
        el.className = 'mt-2 small ' + cls;
        el.textContent = msg;
        el.classList.remove('d-none');
    },

    _hideStatus(): void {
        const el = document.getElementById('tracking-status-msg');
        if (el) el.classList.add('d-none');
    },
};
